import { refs, domReady } from '../modules/utils.js';

class DownloadFormOverlay extends HTMLElement {
  static get observedAttributes() {
    return ['data-open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-open') {
      document.documentElement.toggleAttribute('data-unscrollable', newValue === '');
    }
  }

  async connectedCallback() {
    await domReady();
    this.refs = refs(this);
    this.#listeners();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.escListener);
    document.removeEventListener(this.openEventName, this.openListener);
    document.removeEventListener('click', this.triggerListener);
  }

  #listeners() {
    this.refs.close?.addEventListener('pointerup', () => this.close());
    this.refs.backdrop?.addEventListener('pointerup', () => this.close());

    this.escListener = (event) => {
      if (event.key === 'Escape' && this.hasAttribute('data-open')) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escListener);

    // each overlay listens on its own open event, named after its id, so
    // several overlays can live on the page at once (one per download type)
    // without one open call accidentally triggering another, e.g.
    // dispatchEvent('download-form-clinical-study:open') from utils.js
    this.openEventName = this.id ? `${this.id}:open` : 'download-form-overlay:open';
    this.openListener = () => this.open();
    document.addEventListener(this.openEventName, this.openListener);

    // or, without any JS, by adding data-download-form-trigger="<overlay id>"
    // to a link/button anywhere on the page
    this.triggerListener = (event) => {
      const trigger = event.target.closest('[data-download-form-trigger]');
      if (!trigger) return;

      const targetId = trigger.getAttribute('data-download-form-trigger');
      if (targetId && targetId !== this.id) return;

      event.preventDefault();
      this.open();
    };
    document.addEventListener('click', this.triggerListener);

    this.refs.form?.addEventListener('submit', (event) => this.#onSubmit(event));
  }

  open() {
    if (this.getAttribute('data-state') === 'success') {
      this.setAttribute('data-state', 'form');
      this.refs.form?.reset();
      this.#setError('');
    }
    this.setAttribute('data-open', '');
  }

  close() {
    this.removeAttribute('data-open');
  }

  async #onSubmit(event) {
    event.preventDefault();

    if (this.submitting) return;
    this.submitting = true;

    this.#setError('');
    this.toggleAttribute('data-loading', true);
    this.refs.submit?.setAttribute('disabled', '');

    try {
      const response = await fetch('/download-form/submit', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(this.refs.form),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        const message = result?.errors
          ? Object.values(result.errors).join(' ')
          : 'Something went wrong, please try again.';
        this.#setError(message);
        return;
      }

      this.#onSuccess(result.downloadUrl);
    } catch (error) {
      this.#setError('Something went wrong, please try again.');
    } finally {
      this.submitting = false;
      this.toggleAttribute('data-loading', false);
      this.refs.submit?.removeAttribute('disabled');
    }
  }

  #setError(message) {
    if (this.refs.error) {
      this.refs.error.textContent = message;
    }
  }

  #onSuccess(downloadUrl) {
    this.setAttribute('data-state', 'success');

    if (!downloadUrl) return;

    if (this.refs.downloadLink) {
      this.refs.downloadLink.href = downloadUrl;
    }

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

customElements.define('download-form-overlay', DownloadFormOverlay);
